import { useMemo, useState, type KeyboardEvent } from 'react'
import { Icon } from '@iconify/react'
import type {
  RaspadinhaDayWinningSchedule,
  RaspadinhaParticipant,
} from '@/types/raspadinha-campaign'
import {
  buildCampaignWinnersCsvFilename,
  collectCampaignWinners,
  downloadCampaignWinnersCsv,
  findParticipantByDocument,
  normalizeParticipantDocument,
  formatReceiptSignedAt,
  formatWinningTimeLabel,
  getWinnerPrizeReceiptSignature,
  type RaspadinhaCampaignWinnerRow,
} from '@/lib/raspadinha-campaigns'
import { HorizontalScrollWithArrows } from '@/components/HorizontalScrollWithArrows'
import { PhoneWhatsAppLink } from '@/components/PhoneWhatsAppLink'
import { WinnerDetailModal } from '@/pages/CampanhaRaspadinhaPage/WinnerDetailModal'
import styles from './RaspadinhaWinnersTable.module.css'

export interface RaspadinhaWinnersTableProps {
  winning_date_schedules: RaspadinhaDayWinningSchedule[]
  participants: RaspadinhaParticipant[]
  slug: string
  showExportButton?: boolean
}

function CellText({ value, muted = false }: { value: string; muted?: boolean }) {
  const trimmed = value.trim()
  return (
    <span className={trimmed ? (muted ? styles.cellMuted : styles.cellText) : styles.cellEmpty}>
      {trimmed || '—'}
    </span>
  )
}

export function RaspadinhaWinnersTable({
  winning_date_schedules,
  participants,
  slug,
  showExportButton = true,
}: RaspadinhaWinnersTableProps) {
  const [selectedWinnerRow, setSelectedWinnerRow] = useState<RaspadinhaCampaignWinnerRow | null>(
    null
  )

  const winnerRows = useMemo(
    () => collectCampaignWinners(winning_date_schedules),
    [winning_date_schedules]
  )

  const participantPhoneByDocument = useMemo(() => {
    const map = new Map<string, string>()
    for (const participant of participants) {
      const normalizedDocument = normalizeParticipantDocument(participant.document)
      const phone = participant.phone.trim()
      if (normalizedDocument && phone) {
        map.set(normalizedDocument, phone)
      }
    }
    return map
  }, [participants])

  const selectedParticipant = useMemo(() => {
    if (!selectedWinnerRow) {
      return null
    }
    return findParticipantByDocument(participants, selectedWinnerRow.winner.document)
  }, [participants, selectedWinnerRow])

  const handleWinnerRowOpen = (row: RaspadinhaCampaignWinnerRow) => {
    setSelectedWinnerRow(row)
  }

  const handleWinnerRowKeyDown = (
    row: RaspadinhaCampaignWinnerRow,
    event: KeyboardEvent<HTMLTableRowElement>
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleWinnerRowOpen(row)
    }
  }

  const handleExportWinnersCsv = () => {
    downloadCampaignWinnersCsv(
      winning_date_schedules,
      buildCampaignWinnersCsvFilename(slug)
    )
  }

  return (
    <>
      {showExportButton ? (
        <div className={styles.toolbar}>
          <p className={styles.summaryHint}>
            {winnerRows.length} ganhador(es) cadastrado(s), ordenados por data e horário de
            contemplação. Clique em uma linha para ver os detalhes completos.
          </p>
          <button
            type="button"
            className={styles.exportListButton}
            onClick={handleExportWinnersCsv}
            disabled={winnerRows.length === 0}
            title={
              winnerRows.length === 0
                ? 'Não há ganhadores para exportar'
                : 'Baixar lista de ganhadores em CSV'
            }
          >
            <Icon icon="mdi:file-export-outline" aria-hidden />
            Exportar CSV
          </button>
        </div>
      ) : (
        <p className={styles.summaryHint}>
          {winnerRows.length} ganhador(es) cadastrado(s), ordenados por data e horário de
          contemplação. Clique em uma linha para ver os detalhes completos.
        </p>
      )}

      {winnerRows.length === 0 ? (
        <p className={styles.emptyState}>Nenhum ganhador cadastrado nesta campanha.</p>
      ) : (
        <>
          <p className={styles.scrollHint}>
            <Icon icon="mdi:gesture-swipe-horizontal" aria-hidden />
            Deslize ou use as setas para ver todas as colunas
          </p>
          <div className={styles.tableRegion}>
        <HorizontalScrollWithArrows scrollClassName={styles.tableScroll}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Dia</th>
                <th>Horário</th>
                <th>Status horário</th>
                <th>Nome</th>
                <th>WhatsApp</th>
                <th>CPF / documento</th>
                <th>Prêmio</th>
                <th>Observações</th>
                <th>Hash da assinatura</th>
                <th>IP da assinatura</th>
                <th>Data e hora da assinatura</th>
                <th>ID interno</th>
              </tr>
            </thead>
            <tbody>
              {winnerRows.map((row) => {
                const receipt = getWinnerPrizeReceiptSignature(row.winner)
                const rowKey = `${row.slotId}-${row.winner.id}`
                const participantPhone =
                  participantPhoneByDocument.get(
                    normalizeParticipantDocument(row.winner.document)
                  ) ?? null

                return (
                  <tr
                    key={rowKey}
                    className={styles.winnerRowClickable}
                    onClick={() => handleWinnerRowOpen(row)}
                    onKeyDown={(event) => handleWinnerRowKeyDown(row, event)}
                    tabIndex={0}
                    aria-label={`Ver detalhes de ${row.winner.name || 'ganhador'}`}
                  >
                    <td>
                      <CellText value={row.dateLabel} />
                    </td>
                    <td>
                      <CellText value={formatWinningTimeLabel(row.slotTime)} />
                    </td>
                    <td>
                      <span
                        className={
                          row.slotIsActive ? styles.statusBadgeActive : styles.statusBadgeInactive
                        }
                      >
                        {row.slotIsActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <CellText value={row.winner.name} />
                    </td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <PhoneWhatsAppLink
                        phone={participantPhone}
                        contactName={row.winner.name}
                      />
                    </td>
                    <td>
                      <CellText value={row.winner.document} />
                    </td>
                    <td>
                      <CellText value={row.winner.prize_label} />
                    </td>
                    <td>
                      <CellText value={row.winner.notes} muted />
                    </td>
                    <td>
                      <span className={styles.cellMono}>{receipt.hash}</span>
                    </td>
                    <td>
                      <span className={styles.cellMono}>{receipt.ip_address}</span>
                    </td>
                    <td>
                      <span className={styles.cellMono}>
                        {formatReceiptSignedAt(receipt.signed_at)}
                      </span>
                    </td>
                    <td>
                      <span className={styles.cellMono}>{row.winner.id}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </HorizontalScrollWithArrows>
          </div>
        </>
      )}

      <WinnerDetailModal
        isOpen={selectedWinnerRow !== null}
        winnerRow={selectedWinnerRow}
        participant={selectedParticipant}
        onClose={() => setSelectedWinnerRow(null)}
      />
    </>
  )
}
